import { useMemo, useState } from 'react';
import { useCrm, crmStore } from '../store';
import { Card, PageTitle, StatutBadge, Tag } from '../ui';
import { formatEUR, formatDateFR } from '../utils';
import { Search, RotateCcw, Copy, Play, AlertCircle, FileSpreadsheet, Users, Mic2, Tags, Archive as ArchiveIcon } from 'lucide-react';
import PrestationForm from '../components/PrestationForm';
import { useConfirm } from '../components/ConfirmDialog';
import type { Prestation } from '../types';
import { toast } from 'sonner';
import { exportXlsx } from '../utils/exportXlsx';

const MOTIFS = [
  { key: 'all', label: 'Tous' },
  { key: 'realisee', label: 'Réalisées', match: (s: string) => s === 'Réalisée' || s === 'Signé' },
  { key: 'refusee', label: 'Refusées', match: (s: string) => /Refus/i.test(s) },
  { key: 'expiree', label: 'Expirées', match: (s: string) => s === 'Expirée' },
  { key: 'pause', label: 'En pause', match: (s: string) => /pause/i.test(s) },
];

type TabKey = 'prestations' | 'clients' | 'artistes' | 'domaines';

const TABS: { key: TabKey; label: string; icon: any }[] = [
  { key: 'prestations', label: 'Prestations', icon: ArchiveIcon },
  { key: 'clients', label: 'Clients', icon: Users },
  { key: 'artistes', label: 'Artistes', icon: Mic2 },
  { key: 'domaines', label: 'Domaines', icon: Tags },
];

export default function Archives() {
  const data = useCrm();
  const confirm = useConfirm();
  const [tab, setTab] = useState<TabKey>('prestations');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState<Prestation | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);

  const archived = useMemo(() => data.prest.filter((p) => p.archivedAt), [data.prest]);

  const filtered = useMemo(() => {
    return archived.filter((p) => {
      const f = MOTIFS.find((x) => x.key === filter);
      if (f?.match && !f.match(p.statut)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (![p.client, p.lieu, p.libelle, p.statut, ...p.artistes, ...(p.domaines || [])].join(' ').toLowerCase().includes(q)) return false;
      }
      return true;
    }).sort((a, b) => (b.archivedAt || '').localeCompare(a.archivedAt || ''));
  }, [archived, search, filter]);

  // Regroupement par dimension
  const grouped = useMemo(() => {
    const dim: 'client' | 'artistes' | 'domaines' =
      tab === 'clients' ? 'client' : tab === 'artistes' ? 'artistes' : 'domaines';
    const map = new Map<string, Prestation[]>();
    for (const p of filtered) {
      if (dim === 'client') {
        const k = (p.client || '—').trim() || '—';
        const arr = map.get(k) ?? []; arr.push(p); map.set(k, arr);
      } else {
        const keys = (p as any)[dim] as string[] | undefined;
        if (!keys || keys.length === 0) {
          const arr = map.get('—') ?? []; arr.push(p); map.set('—', arr);
        } else {
          for (const k of keys) {
            const arr = map.get(k) ?? []; arr.push(p); map.set(k, arr);
          }
        }
      }
    }
    return Array.from(map.entries())
      .map(([name, prests]) => ({
        name,
        prests: [...prests].sort((a, b) => (b.archivedAt || '').localeCompare(a.archivedAt || '')),
        ca: prests.reduce((s, p) => s + (p.propTTC ?? 0), 0),
      }))
      .sort((a, b) => b.prests.length - a.prests.length || a.name.localeCompare(b.name));
  }, [filtered, tab]);

  const handleRestore = async (p: Prestation) => {
    const ok = await confirm({
      title: 'Restaurer cette prestation ?',
      description: `« ${p.client} » repassera en actif.`,
      confirmLabel: 'Restaurer',
    });
    if (!ok) return;
    try {
      await crmStore.restorePrest(p.id);
      toast.success('Prestation restaurée dans les actives.');
    } catch (e: any) {
      toast.error('Erreur lors de la restauration : ' + (e?.message || ''));
    }
  };

  const handleDuplicate = (p: Prestation) => {
    const copy: Prestation = {
      ...p,
      id: '',
      archivedAt: null,
      statut: 'Demande de Rdv',
      dateEvent: '',
      dateCreation: new Date().toISOString().slice(0, 10),
      gcalId: null,
      contratClient: '',
      contratArtiste: '',
      factureEmise: '',
      paye: '',
    };
    setEditing(copy);
    setOpenForm(true);
  };

  const runAutoArchive = async () => {
    setAutoLoading(true);
    try {
      const r = await crmStore.runAutoArchive();
      toast.success(`Auto-archivage : ${r.realisees} réalisée(s), ${r.refusees} refusée(s), ${r.expirees} expirée(s), ${r.brouillons_abandonnes ?? 0} brouillon(s) abandonné(s).`);
    } catch (e: any) {
      toast.error('Erreur : ' + (e?.message || ''));
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <div>
      <PageTitle
        title="Archives"
        subtitle={`${archived.length} prestation(s) archivée(s) · ${filtered.length} affichée(s)`}
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const stamp = new Date().toISOString().slice(0, 10);
                exportXlsx(`archives-${stamp}.xlsx`, filtered, [
                  { header: 'N°', key: 'numero' as any, width: 14 },
                  { header: 'Statut', key: 'statut' as any, width: 22 },
                  { header: 'Client', key: 'client' as any, width: 28 },
                  { header: 'Date événement', key: 'dateEvent' as any, format: 'date', width: 14 },
                  { header: 'Lieu', key: 'lieu' as any, width: 32 },
                  { header: 'Domaines', key: (r: any) => (r.domaines || []).join(', '), width: 24 },
                  { header: 'Services', key: (r: any) => (r.services || []).join(', '), width: 24 },
                  { header: 'Artistes', key: (r: any) => (r.artistes || []).join(', '), width: 28 },
                  { header: 'Prop. TTC', key: 'propTTC' as any, format: 'currency', width: 14 },
                  { header: 'Devis', key: 'devis' as any, format: 'currency', width: 14 },
                  { header: 'Structure', key: 'structure' as any, width: 16 },
                  { header: 'Archivé le', key: 'archivedAt' as any, format: 'date', width: 14 },
                  { header: 'Créé le', key: 'dateCreation' as any, format: 'date', width: 14 },
                ], 'Archives');
              }}
              title="Exporter en Excel"
              className="px-3 py-2.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 font-medium rounded-lg text-sm transition-colors inline-flex items-center gap-2"
            >
              <FileSpreadsheet size={14} strokeWidth={1.5} /> Excel
            </button>
            <button
              onClick={runAutoArchive}
              disabled={autoLoading}
              className="px-4 py-2.5 bg-sky-500/15 hover:bg-sky-500/25 text-sky-400 border border-sky-500/40 font-medium rounded-lg text-sm transition-colors inline-flex items-center gap-2"
            >
              <Play size={14} strokeWidth={1.5} /> {autoLoading ? 'Archivage…' : 'Lancer auto-archivage'}
            </button>
          </div>
        }
      />

      <PrestationForm open={openForm} onOpenChange={setOpenForm} initial={editing} />

      {/* Onglets */}
      <div className="flex gap-1 mb-4 border-b border-slate-800">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                active ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              <Icon size={14} strokeWidth={1.5} /> {t.label}
            </button>
          );
        })}
      </div>

      <Card className="p-3 mb-4 bg-sky-500/5 border-sky-500/20">
        <div className="flex items-start gap-2 text-xs text-slate-300">
          <AlertCircle size={14} className="text-sky-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
          <div>
            <strong className="text-sky-400">Auto-archivage quotidien (3h) :</strong> Les prestations <strong>Signé</strong> dont la date est passée deviennent <strong>Réalisée</strong> et sont archivées. Les <strong>Refusée</strong> sont archivées immédiatement. Les prestations en attente de réponse client dont la date est dépassée de plus de <strong>30 jours</strong> deviennent <strong>Expirée</strong> et sont archivées.
          </div>
        </div>
      </Card>

      {/* Filtres communs */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Rechercher dans les archives ${tab === 'prestations' ? '' : '· ' + TABS.find(t => t.key === tab)?.label.toLowerCase()}…`}
            className="w-full pl-9 pr-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {MOTIFS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                filter === f.key ? 'bg-amber-500/15 text-amber-400 border-amber-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'prestations' ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 text-[10px] text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-3 text-left">Archivée le</th>
                  <th className="px-3 py-3 text-left">Client</th>
                  <th className="px-3 py-3 text-left">Date prestation</th>
                  <th className="px-3 py-3 text-left">Lieu</th>
                  <th className="px-3 py-3 text-left">Statut</th>
                  <th className="px-3 py-3 text-left">Domaines</th>
                  <th className="px-3 py-3 text-right">Prop. TTC</th>
                  <th className="px-3 py-3 text-center w-[200px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-slate-800/60 hover:bg-slate-800/30 transition-colors align-top">
                    <td className="px-3 py-3 text-xs text-slate-400 whitespace-nowrap">
                      {p.archivedAt ? formatDateFR(p.archivedAt, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-3 py-3 max-w-[200px]">
                      <div className="font-medium text-slate-200 text-sm">{p.client}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{p.libelle} · {p.demande}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-amber-400 whitespace-nowrap">
                      {p.dateEvent ? formatDateFR(p.dateEvent, { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-300 max-w-[140px] truncate">{p.lieu || '—'}</td>
                    <td className="px-3 py-3"><StatutBadge statut={p.statut} /></td>
                    <td className="px-3 py-3"><div className="flex flex-wrap gap-1 max-w-[160px]">{p.domaines.slice(0, 2).map((s) => <Tag key={s} color="violet">{s}</Tag>)}{p.domaines.length > 2 && <Tag>+{p.domaines.length - 2}</Tag>}</div></td>
                    <td className="px-3 py-3 text-right text-amber-400 text-sm">{p.propTTC != null ? formatEUR(p.propTTC) : '—'}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => handleRestore(p)}
                          title="Restaurer en actif"
                          className="px-2 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded text-[10px] inline-flex items-center gap-1"
                        >
                          <RotateCcw size={10} strokeWidth={1.5} /> Restaurer
                        </button>
                        <button
                          onClick={() => handleDuplicate(p)}
                          title="Dupliquer pour nouvelle date"
                          className="px-2 py-1.5 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/30 rounded text-[10px] inline-flex items-center gap-1"
                        >
                          <Copy size={10} strokeWidth={1.5} /> Dupliquer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-500 text-sm">Aucune archive.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ name, prests, ca }) => {
            const TabIcon = TABS.find((t) => t.key === tab)!.icon;
            return (
              <Card key={name} className="overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading text-lg text-slate-100 flex items-center gap-2">
                      <TabIcon size={16} className="text-amber-400" strokeWidth={1.5} /> {name}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      {prests.length} prestation(s) archivée(s)
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">CA cumulé</div>
                    <div className="text-amber-400 font-heading text-lg">{formatEUR(ca)}</div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[820px]">
                    <thead className="bg-slate-900/40 text-[10px] text-slate-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-2 text-left">Archivée le</th>
                        <th className="px-4 py-2 text-left">Date event</th>
                        <th className="px-4 py-2 text-left">Libellé</th>
                        <th className="px-4 py-2 text-left">Statut</th>
                        {tab !== 'clients' && <th className="px-4 py-2 text-left">Client</th>}
                        {tab !== 'artistes' && <th className="px-4 py-2 text-left">Artistes</th>}
                        <th className="px-4 py-2 text-right">Prop. TTC</th>
                        <th className="px-4 py-2 text-right w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {prests.map((p) => (
                        <tr key={p.id} className="border-t border-slate-800/60 hover:bg-slate-800/30">
                          <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{p.archivedAt ? formatDateFR(p.archivedAt) : '—'}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-400">{formatDateFR(p.dateEvent)}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-300">{p.libelle}</td>
                          <td className="px-4 py-2.5"><StatutBadge statut={p.statut} /></td>
                          {tab !== 'clients' && <td className="px-4 py-2.5 text-xs text-slate-300">{p.client || '—'}</td>}
                          {tab !== 'artistes' && <td className="px-4 py-2.5 text-xs text-slate-300">{p.artistes?.join(', ') || '—'}</td>}
                          <td className="px-4 py-2.5 text-xs text-right text-amber-400">
                            {p.propTTC != null ? formatEUR(p.propTTC) : '—'}
                          </td>
                          <td className="px-2 py-2.5 text-right">
                            <button
                              title="Restaurer en actif"
                              onClick={() => handleRestore(p)}
                              className="p-1.5 rounded-md text-slate-500 hover:text-emerald-400 hover:bg-slate-800 transition-colors"
                            >
                              <RotateCcw size={14} strokeWidth={1.5} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
          {grouped.length === 0 && (
            <Card className="p-8 text-center text-sm text-slate-500">Aucune archive.</Card>
          )}
        </div>
      )}
    </div>
  );
}
